import { useEffect, useState } from 'react';
import ErrorBanner from '../../components/ErrorBanner';
import Modal from '../../components/Modal';
import {
  createBibleReading,
  deleteBibleReading,
  listBibleReadings,
  updateBibleReading,
} from '../../api/bibleReadings';
import { listUsers } from '../../api/users';
import { API_BASE_URL } from '../../config';

export default function BibleReadingsSection({ token }) {
  const [readings, setReadings] = useState([]);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [pdfFile, setPdfFile] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editError, setEditError] = useState('');
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [busyReadingId, setBusyReadingId] = useState(null);

  const load = async () => {
    setIsLoading(true);
    setError('');
    try {
      const [readingsData, usersData] = await Promise.all([
        listBibleReadings(token),
        listUsers(token),
      ]);
      setReadings(readingsData);
      setUsers(usersData);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const uploaderName = (userId) => users.find((u) => u.user_id === userId)?.name || `#${userId}`;

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!pdfFile) {
      setSaveError('Select a PDF file');
      return;
    }
    setIsSaving(true);
    setSaveError('');
    setSaveSuccess('');
    try {
      const formData = new FormData();
      formData.append('title', title);
      if (description) formData.append('description', description);
      formData.append('pdf', pdfFile);

      await createBibleReading(token, formData);
      setTitle('');
      setDescription('');
      setPdfFile(null);
      setSaveSuccess('Reading uploaded');
      await load();
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const openEdit = (reading) => {
    setEditing(reading);
    setEditTitle(reading.title);
    setEditDescription(reading.description || '');
    setEditError('');
    setEditModalOpen(true);
  };

  const closeEditModal = () => setEditModalOpen(false);

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setIsSavingEdit(true);
    setEditError('');
    try {
      await updateBibleReading(token, editing.reading_id, {
        title: editTitle,
        description: editDescription,
      });
      closeEditModal();
      await load();
    } catch (err) {
      setEditError(err.message);
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleDelete = async (reading) => {
    if (!window.confirm(`Delete reading "${reading.title}"?`)) return;
    setBusyReadingId(reading.reading_id);
    setError('');
    try {
      await deleteBibleReading(token, reading.reading_id);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyReadingId(null);
    }
  };

  return (
    <section>
      <h2 className="text-xl font-semibold text-gray-900 mb-6">Sunday Scripture Reading</h2>

      <form
        onSubmit={handleUpload}
        className="bg-white border border-gray-200 rounded-lg p-4 mb-6 space-y-3"
      >
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
          <input
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Day 1 - Genesis 1-3"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description (optional)
          </label>
          <textarea
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">PDF file</label>
          <input
            required
            type="file"
            accept=".pdf"
            onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
            className="w-full text-sm"
          />
        </div>

        {saveError && <p className="text-sm text-red-600">{saveError}</p>}
        {saveSuccess && <p className="text-sm text-indigo-600">{saveSuccess}</p>}

        <button
          type="submit"
          disabled={isSaving}
          className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          {isSaving ? 'Uploading...' : 'Upload reading'}
        </button>
      </form>

      <ErrorBanner message={error} onRetry={load} />

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left px-4 py-2 font-medium text-gray-500">Title</th>
              <th className="text-left px-4 py-2 font-medium text-gray-500">Description</th>
              <th className="text-left px-4 py-2 font-medium text-gray-500">Uploaded by</th>
              <th className="text-left px-4 py-2 font-medium text-gray-500">Uploaded at</th>
              <th className="text-right px-4 py-2 font-medium text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading ? (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-gray-400">
                  Loading...
                </td>
              </tr>
            ) : readings.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-gray-400">
                  No readings uploaded yet
                </td>
              </tr>
            ) : (
              readings.map((r) => {
                const isBusy = busyReadingId === r.reading_id;
                return (
                  <tr key={r.reading_id}>
                    <td className="px-4 py-2 text-gray-900">{r.title}</td>
                    <td className="px-4 py-2 text-gray-600 max-w-md truncate">
                      {r.description || '—'}
                    </td>
                    <td className="px-4 py-2 text-gray-600">{uploaderName(r.uploaded_by)}</td>
                    <td className="px-4 py-2 text-gray-600 whitespace-nowrap">
                      {new Date(r.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-2 text-right space-x-3 whitespace-nowrap">
                      <a
                        href={`${API_BASE_URL}/bible-readings/${r.reading_id}/download`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-indigo-600 hover:underline"
                      >
                        View
                      </a>
                      <button
                        onClick={() => openEdit(r)}
                        disabled={isBusy}
                        className="text-gray-600 hover:underline disabled:opacity-50"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(r)}
                        disabled={isBusy}
                        className="text-red-600 hover:underline disabled:opacity-50"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        </div>
      </div>

      {editModalOpen && (
        <Modal title="Edit reading" onClose={closeEditModal}>
          <form onSubmit={handleSaveEdit} className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input
                required
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description (optional)
              </label>
              <textarea
                rows={2}
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            {editError && <p className="text-sm text-red-600">{editError}</p>}
            <button
              type="submit"
              disabled={isSavingEdit}
              className="w-full rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {isSavingEdit ? 'Saving...' : 'Save'}
            </button>
          </form>
        </Modal>
      )}
    </section>
  );
}
